import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Building2,
  ClipboardList,
  Cog,
  Database,
  Download,
  History,
  Plus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CHECKLIST_FREQUENCIES,
  FREQUENCY_LABELS,
  PERMISSIONS,
  ROLES,
  SHIFTS,
  newId,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  ListRow,
  Modal,
  Select,
  SkeletonRows,
  Spinner,
  StatusPill,
  Textarea,
} from '../../components/ui';
import { ApiError, api, tokens } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatBytes, formatDateTime, humanise, relativeTime } from '../../lib/format';

/**
 * Each area carries its own permission.
 *
 * why: these are genuinely different jobs. An AM Coach authors checklists but
 * has no business managing users; an administrator manages users but should
 * not be quietly granted the ability to change engineering configuration.
 */
const TABS = [
  { key: 'org', label: 'Factory & lines', icon: Building2, permission: PERMISSIONS.ORG_VIEW },
  { key: 'users', label: 'People', icon: Users, permission: PERMISSIONS.USER_MANAGE },
  {
    key: 'checklists',
    label: 'Checklists',
    icon: ClipboardList,
    permission: PERMISSIONS.TEMPLATE_VIEW,
  },
  { key: 'settings', label: 'Settings', icon: Cog, permission: PERMISSIONS.SETTINGS_MANAGE },
  { key: 'backups', label: 'Backups', icon: Database, permission: PERMISSIONS.BACKUP_MANAGE },
  { key: 'audit', label: 'Audit log', icon: History, permission: PERMISSIONS.AUDIT_VIEW },
] as const;

export function AdminPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);

  const visible = TABS.filter((item) => user?.permissions.includes(item.permission));
  const active = tab && visible.some((t) => t.key === tab) ? tab : (visible[0]?.key ?? 'org');

  if (visible.length === 0) {
    return (
      <div className="p-6">
        <Callout tone="warn" title="Nothing here for your role">
          Your role does not include any administration area.
        </Callout>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Administration" subtitle="Configure ODR without changing code" />

      <div className="border-b border-steel-200 bg-white px-4 lg:px-6">
        <nav className="flex gap-1 overflow-x-auto" aria-label="Administration sections">
          {visible.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => navigate(`/admin/${item.key}`)}
              className={clsx(
                'flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm font-semibold whitespace-nowrap',
                active === item.key
                  ? 'border-info-600 text-info-700'
                  : 'border-transparent text-steel-500 hover:text-steel-800',
              )}
            >
              <item.icon aria-hidden className="size-4" />
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-4 lg:p-6">
        {active === 'org' ? <OrgTab /> : null}
        {active === 'users' ? <UsersTab /> : null}
        {active === 'checklists' ? <ChecklistsTab /> : null}
        {active === 'settings' ? <SettingsTab /> : null}
        {active === 'backups' ? <BackupsTab /> : null}
        {active === 'audit' ? <AuditTab /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface TreeFactory {
  id: string;
  code: string;
  name: string;
  areas: Array<{
    id: string;
    code: string;
    name: string;
    lines: Array<{
      id: string;
      code: string;
      name: string;
      format: string | null;
      machines: Array<{ id: string; code: string; name: string; status: string; isActive: boolean }>;
    }>;
  }>;
}

function OrgTab() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['org', 'tree'],
    queryFn: () => api.get<{ data: TreeFactory[] }>('/api/org/tree'),
  });

  if (isLoading) return <Spinner label="Loading structure" />;

  return (
    <div className="space-y-4">
      <Callout tone="info">
        Factory → Area → Line → Machine → Component. Machines carry the QR label operators scan.
      </Callout>

      {(data?.data ?? []).map((factory) => (
        <Card key={factory.id}>
          <CardHeader title={`${factory.code} — ${factory.name}`} />
          {factory.areas.map((area) => (
            <div key={area.id} className="border-t border-steel-100">
              <p className="bg-steel-50 px-4 py-2 text-xs font-extrabold tracking-widest text-steel-600 uppercase">
                {area.name}
              </p>
              {area.lines.map((line) => (
                <div key={line.id} className="border-t border-steel-100 px-4 py-3">
                  <p className="text-sm font-bold text-steel-900">
                    {line.name}
                    {line.format ? (
                      <Badge className="ml-2">{line.format}</Badge>
                    ) : null}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {line.machines.map((machine) => (
                      <li key={machine.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/machines/${machine.id}`)}
                          className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left hover:bg-steel-50"
                        >
                          <span className="text-sm text-steel-800">
                            <span className="font-mono font-semibold text-info-700">
                              {machine.code}
                            </span>{' '}
                            {machine.name}
                          </span>
                          <StatusPill
                            tone={machine.isActive ? 'ok' : 'neutral'}
                            label={humanise(machine.status)}
                          />
                        </button>
                      </li>
                    ))}
                    {line.machines.length === 0 ? (
                      <li className="px-2 py-1.5 text-sm text-steel-400">No machines on this line</li>
                    ) : null}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface UserRow {
  id: string;
  username: string;
  fullName: string;
  employeeNo: string | null;
  shift: string | null;
  jobTitle: string | null;
  status: string;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  defaultLineName: string | null;
  roles: string[];
}

function UsersTab() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: UserRow[] }>('/api/users?pageSize=200'),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button icon={Plus} onClick={() => setCreateOpen(true)}>
          Add person
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <ul className="divide-y divide-steel-100">
            {(data?.data ?? []).map((user) => (
              <li key={user.id} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-steel-900">
                    {user.fullName}
                    <span className="ml-2 font-mono text-xs font-normal text-steel-500">
                      {user.username}
                    </span>
                  </p>
                  <p className="text-xs text-steel-500">
                    {user.jobTitle ?? '—'}
                    {user.shift ? ` · Shift ${user.shift}` : ''}
                    {user.defaultLineName ? ` · ${user.defaultLineName}` : ''}
                  </p>
                  <p className="text-xs text-steel-400">
                    {user.lastLoginAt
                      ? `Last signed in ${relativeTime(user.lastLoginAt)}`
                      : 'Never signed in'}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <Badge key={role}>{humanise(role)}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <StatusPill
                    tone={user.status === 'ACTIVE' ? 'ok' : 'neutral'}
                    label={humanise(user.status)}
                  />
                  {user.mustChangePassword ? <Badge tone="warn">Must set password</Badge> : null}
                  <Link
                    to={`/training/${user.id}`}
                    className="text-xs font-bold text-info-700 hover:underline"
                  >
                    Training →
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {createOpen ? (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['users'] });
            setCreateOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    employeeNo: '',
    jobTitle: '',
    password: '',
    shift: '',
    roleCode: 'OPERATOR',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/users', {
        id: newId(),
        username: form.username.trim().toLowerCase(),
        fullName: form.fullName.trim(),
        employeeNo: form.employeeNo || null,
        jobTitle: form.jobTitle || null,
        password: form.password,
        shift: form.shift || null,
        roleCodes: [form.roleCode],
        mustChangePassword: true,
      }),
    onSuccess: onCreated,
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.details?.map((d) => d.message).join(' ') ?? caught.message)
          : 'Could not create this person.',
      ),
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a person"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={create.isPending}
            disabled={!form.username || !form.fullName || form.password.length < 10}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        <Field label="Full name" required>
          <Input value={form.fullName} onChange={(e) => set('fullName')(e.target.value)} />
        </Field>
        <Field label="Username" required hint="Letters, numbers, dot, dash or underscore.">
          <Input
            value={form.username}
            onChange={(e) => set('username')(e.target.value)}
            autoCapitalize="none"
            spellCheck={false}
          />
        </Field>
        <Field label="Employee number">
          <Input value={form.employeeNo} onChange={(e) => set('employeeNo')(e.target.value)} />
        </Field>
        <Field label="Job title">
          <Input value={form.jobTitle} onChange={(e) => set('jobTitle')(e.target.value)} />
        </Field>
        <Field label="Role" required>
          <Select value={form.roleCode} onChange={(e) => set('roleCode')(e.target.value)}>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {humanise(role)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Shift">
          <Select value={form.shift} onChange={(e) => set('shift')(e.target.value)}>
            <option value="">Not on a fixed shift</option>
            {SHIFTS.map((shift) => (
              <option key={shift} value={shift}>
                Shift {shift}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Starting password"
          required
          hint="At least 10 characters. They must change it the first time they sign in."
        >
          <Input
            type="text"
            value={form.password}
            onChange={(e) => set('password')(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function ChecklistsTab() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          code: string;
          name: string;
          frequency: string;
          isActive: boolean;
          currentVersion: { versionNo: number; publishedAt: string | null } | null;
          draftVersion: { id: string; versionNo: number } | null;
          versions: Array<{ id: string; versionNo: number; status: string }>;
        }>;
      }>('/api/checklist-templates?includeInactive=true'),
  });

  if (isLoading) return <Spinner label="Loading checklists" />;

  return (
    <div className="space-y-4">
      <Callout tone="info">
        Checklists go through draft → review → approved → published. Publishing a new version
        never rewrites history: completed runs stay linked to the version they were performed
        against.
      </Callout>

      <div className="flex justify-end">
        <Button icon={Plus} onClick={() => setCreateOpen(true)}>
          New checklist
        </Button>
      </div>

      <Card>
        <ul className="divide-y divide-steel-100">
          {(data?.data ?? []).map((template) => (
            <li key={template.id}>
              <ListRow onClick={() => navigate(`/admin/checklists/${template.id}`)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-steel-900">
                      <span className="font-mono text-info-700">{template.code}</span>{' '}
                      {template.name}
                    </p>
                    <p className="text-xs text-steel-500">
                      {humanise(template.frequency)} ·{' '}
                      {template.currentVersion
                        ? `v${template.currentVersion.versionNo} published`
                        : 'Not published yet'}
                      {template.draftVersion ? ` · v${template.draftVersion.versionNo} draft open` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <StatusPill
                      tone={template.isActive ? 'ok' : 'neutral'}
                      label={template.isActive ? 'In use' : 'Inactive'}
                    />
                    {template.draftVersion ? <Badge tone="warn">Draft</Badge> : null}
                  </div>
                </div>
              </ListRow>
            </li>
          ))}
        </ul>
      </Card>

      {createOpen ? (
        <CreateTemplateModal
          onClose={() => setCreateOpen(false)}
          onCreated={(templateId) => {
            setCreateOpen(false);
            navigate(`/admin/checklists/${templateId}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateTemplateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (templateId: string) => void;
}) {
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    frequency: 'DAILY',
    machineType: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ template: { id: string } }>('/api/checklist-templates', {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description || null,
        frequency: form.frequency,
        machineType: form.machineType || null,
      }),
    onSuccess: (result) => onCreated(result.template.id),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the checklist.'),
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title="New checklist"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={create.isPending}
            disabled={!form.code.trim() || !form.name.trim()}
            onClick={() => create.mutate()}
          >
            Create and start editing
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? <Callout tone="alarm">{error}</Callout> : null}
        <Callout tone="info">
          The checklist is created as an editable draft. Add sections and tasks, then submit it
          for review.
        </Callout>

        <Field label="Code" required hint="Short, unique. e.g. CILT-FILL-DAILY">
          <Input
            value={form.code}
            onChange={(e) => set('code')(e.target.value)}
            autoCapitalize="characters"
            spellCheck={false}
          />
        </Field>
        <Field label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => set('name')(e.target.value)}
            placeholder="Daily CILT — Filling Machine"
          />
        </Field>
        <Field label="How often" required>
          <Select value={form.frequency} onChange={(e) => set('frequency')(e.target.value)}>
            {CHECKLIST_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Intended machine type" hint="Optional. Helps whoever schedules it.">
          <Input
            value={form.machineType}
            onChange={(e) => set('machineType')(e.target.value)}
            placeholder="Filling Machine"
          />
        </Field>
        <Field label="Description">
          <Textarea
            value={form.description}
            onChange={(e) => set('description')(e.target.value)}
            rows={3}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function SettingsTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () =>
      api.get<{
        data: Array<{
          key: string;
          value: string | null;
          valueType: string;
          category: string;
          description: string | null;
        }>;
      }>('/api/admin/settings'),
  });

  const save = useMutation({
    mutationFn: (input: { key: string; value: string }) =>
      api.put(`/api/admin/settings/${encodeURIComponent(input.key)}`, { value: input.value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      setEditing(null);
    },
  });

  if (isLoading) return <Spinner label="Loading settings" />;

  type Setting = NonNullable<typeof data>['data'][number];
  const byCategory = (data?.data ?? []).reduce<Record<string, Setting[]>>((acc, setting) => {
    (acc[setting.category] ??= []).push(setting);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(byCategory).map(([category, settings]) => (
        <Card key={category}>
          <CardHeader title={humanise(category)} />
          <ul className="divide-y divide-steel-100">
            {settings.map((setting) => (
              <li key={setting.key} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-steel-500">{setting.key}</p>
                  <p className="text-sm font-semibold text-steel-900">{setting.value ?? '—'}</p>
                  {setting.description ? (
                    <p className="mt-0.5 text-xs text-steel-500">{setting.description}</p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditing({ key: setting.key, value: setting.value ?? '' })}
                >
                  Change
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {editing ? (
        <Modal
          open
          onClose={() => setEditing(null)}
          title="Change setting"
          footer={
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button fullWidth loading={save.isPending} onClick={() => save.mutate(editing)}>
                Save
              </Button>
            </div>
          }
        >
          <Field label={editing.key}>
            <Input
              value={editing.value}
              onChange={(event) => setEditing({ ...editing, value: event.target.value })}
            />
          </Field>
          <p className="mt-2 text-xs text-steel-500">
            Booleans accept true or false. This change is recorded in the audit log.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function BackupsTab() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () =>
      api.get<{ data: Array<{ filename: string; sizeBytes: number; createdAt: string }> }>(
        '/api/admin/backups',
      ),
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/admin/backups', { label: 'manual' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['backups'] }),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create a backup.'),
  });

  const download = async (filename: string) => {
    const response = await fetch(`/api/admin/backups/${encodeURIComponent(filename)}`, {
      headers: { Authorization: `Bearer ${tokens.access() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {error ? <Callout tone="alarm">{error}</Callout> : null}

      <Callout tone="warn" title="Restoring is a manual procedure">
        A backup is a consistent snapshot taken while ODR keeps running. Restoring replaces all
        operational data and must be done with the server stopped — the app will not do it live.
        Download a backup and follow the restore steps with your administrator.
      </Callout>

      <div className="flex justify-end">
        <Button icon={Database} loading={create.isPending} onClick={() => create.mutate()}>
          Create backup now
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : !data?.data.length ? (
          <EmptyState
            icon={Database}
            title="No backups yet"
            message="Create one now, and set up a scheduled copy of the backups folder to another machine."
          />
        ) : (
          <ul className="divide-y divide-steel-100">
            {data.data.map((backup) => (
              <li
                key={backup.filename}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-steel-900">{backup.filename}</p>
                  <p className="text-xs text-steel-500">
                    {formatBytes(backup.sizeBytes)} · {formatDateTime(backup.createdAt)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={Download}
                  onClick={() => void download(backup.filename)}
                >
                  Download
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AuditTab() {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['audit', search],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          action: string;
          entityType: string;
          summary: string;
          userLabel: string | null;
          createdAt: string;
          ip: string | null;
        }>;
      }>(`/api/audit?pageSize=100${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  });

  return (
    <div className="space-y-4">
      <Card className="p-3">
        <Field label="Search the audit log">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Person, machine, tag number…"
          />
        </Field>
      </Card>

      <Card>
        {isLoading ? (
          <SkeletonRows rows={8} />
        ) : !data?.data.length ? (
          <EmptyState
            icon={History}
            title="Nothing recorded"
            message="No audit entries match this search."
          />
        ) : (
          <ul className="divide-y divide-steel-100">
            {data.data.map((entry) => (
              <li key={entry.id} className="px-4 py-2.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm text-steel-900">{entry.summary}</p>
                  <p className="text-xs text-steel-400">{formatDateTime(entry.createdAt)}</p>
                </div>
                <p className="mt-0.5 text-xs text-steel-500">
                  <Badge>{humanise(entry.action)}</Badge>{' '}
                  <span className="ml-1">{entry.userLabel ?? 'System'}</span>
                  {entry.ip ? <span className="ml-2 font-mono">{entry.ip}</span> : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
